// @ts-nocheck
/*
 * Port of views/dashboard/agent-leads/dashboard-overview.js.
 * APIs: API_AGENT_LEADS_LIST + API_AGENT_LEADS_GET_ONE.
 * Client-side filter by search/status/date, per-row detail modal on click.
 * Column count preserved: 9 (Title, Contact Group, Assigned To, Form,
 * Completed, Total, Status, Assigned By, Assigned At).
 */
import { useEffect, useMemo, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Download } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_LEADS_LIST, API_AGENT_LEADS_GET_ONE, API_AGENT_LEADS_ITEMS } from '@/service/url';
import { LeadsPager, usePagedLeads } from '@/features/agent-leads/usePagedLeads';
import { exportToCSV } from '@/utilities/helpers';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const toLocalDateStr = (d) => {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : '—');

function StatusBadge({ status }) {
  const isCompleted = status === 'completed';
  return (
    <span className={
      'rounded-full px-2 py-0.5 text-[11px] font-semibold ' +
      (isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')
    }>
      {isCompleted ? 'Completed' : 'Pending'}
    </span>
  );
}

const agentNames = (row) => {
  const names = Array.isArray(row?.agents)
    ? row.agents.map((a) => a.agent_name || a.agent_number || `#${a.id}`)
    : [];
  if (names.length) return names.join(', ');
  const ids = Array.isArray(row?.agent_ids) ? row.agent_ids : [];
  return ids.length ? ids.map((id) => `#${id}`).join(', ') : '—';
};

const assignedToLabel = (row) => {
  if (row?.assignment_type === 'call_group') {
    return `Call Group: ${row.call_group?.group_name || row.call_group_id || '—'}`;
  }
  return agentNames(row);
};

export default function AgentLeadsDashboard() {
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState([]);
  const [search, setSearch] = usePersistedState('dashboard/AgentLeadsDashboard:search', '');
  const [statusFilter, setStatusFilter] = usePersistedState('dashboard/AgentLeadsDashboard:status', 'all');
  const today = toLocalDateStr(new Date());
  const [detailId, setDetailId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const detailItems = usePagedLeads(detailId ? API_AGENT_LEADS_ITEMS.replace(':id', detailId) : null, {}, 20);

  const fetchAssignments = () => {
    setLoading(true);
    getApiCall(API_AGENT_LEADS_LIST)
      .then((resp) => setAssignments(Array.isArray(resp) ? resp : []))
      .catch((err) => toast.error(friendlyApiError(err) || 'Unable to load agent leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAssignments(); }, []);

  const openDetail = (id) => {
    setDetailId(id);
    setDetailData(null);
    setDetailLoading(true);
    getApiCall(`${API_AGENT_LEADS_GET_ONE.replace(':id', id)}?items=0`)
      .then((resp) => setDetailData(resp))
      .catch((err) => toast.error(friendlyApiError(err) || 'Unable to load assignment'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => { setDetailId(null); setDetailData(null); };

  const filteredAssignments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      const day = r.created_at ? toLocalDateStr(new Date(r.created_at)) : '';
      if (day !== today) return false;
      if (q) {
        const hay = [
          r.title, r.contact_group?.group_name, r.disposition_form?.name,
          assignedToLabel(r), r.assigned_by_user?.username,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [assignments, search, statusFilter, today]);

  const handleExport = () => {
    if (!filteredAssignments.length) return toast.info('No agent leads to export');
    exportToCSV({
      fileName: 'agent-leads-overview',
      data: filteredAssignments,
      columns: [
        { header: 'Title', accessor: (r) => r.title || '-' },
        { header: 'Contact Group', accessor: (r) => r.contact_group?.group_name || '-' },
        { header: 'Assigned To', accessor: (r) => assignedToLabel(r) },
        { header: 'Disposition Form', accessor: (r) => r.disposition_form?.name || '-' },
        { header: 'Completed', accessor: (r) => r.completed_leads || 0 },
        { header: 'Total', accessor: (r) => r.total_leads || 0 },
        { header: 'Status', accessor: (r) => r.status || '-' },
        { header: 'Assigned By', accessor: (r) => r.assigned_by_user?.username || '-' },
        { header: 'Assigned At', accessor: (r) => fmtDateTime(r.created_at) },
      ],
    });
  };

  const resetFilters = () => {
    setSearch(''); setStatusFilter('all');
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      { accessorKey: 'title', header: 'Title', cell: ({ row }) => row.original.title || '-' },
      { id: 'contact_group', header: 'Contact Group', cell: ({ row }) => row.original.contact_group?.group_name || '-' },
      { id: 'assigned_to', header: 'Assigned To', cell: ({ row }) => assignedToLabel(row.original) },
      { id: 'disposition_form', header: 'Disposition Form', cell: ({ row }) => row.original.disposition_form?.name || '-' },
      { accessorKey: 'completed_leads', header: 'Completed', cell: ({ row }) => row.original.completed_leads || 0 },
      { accessorKey: 'total_leads', header: 'Total', cell: ({ row }) => row.original.total_leads || 0 },
      { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} /> },
      { id: 'assigned_by', header: 'Assigned By', cell: ({ row }) => row.original.assigned_by_user?.username || '-' },
      { accessorKey: 'created_at', header: 'Assigned At', cell: ({ row }) => fmtDateTime(row.original.created_at) },
    ],
    [],
  );

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-bold text-foreground">
          Agent Leads Overview
          <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">Today only</span>
        </span>
        {filteredAssignments.length > 0 && (
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="mr-1.5 h-3.5 w-3.5" />Export
          </Button>
        )}
      </div>

      <DateRangeFilter
        compact
        hideDates
        className="mb-3"
        value={{ from: today, to: today }}
        onChange={() => {}}
        onReset={resetFilters}
        actions={<Button variant="outline" className="h-9" onClick={fetchAssignments}>Refresh</Button>}
      >
        <div className="min-w-[180px] flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Search</label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, form..." className="h-9" />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 w-full min-w-[140px] rounded-md border border-border bg-background px-2 text-sm">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </DateRangeFilter>

      <DataTable
        tableId="dashboard.agent-leads"
        columns={columns}
        data={filteredAssignments}
        isLoading={loading}
        emptyTitle="No agent leads"
        emptyDescription="No agent leads found for the current filters."
        pageSize={10}
        onRowClick={(row) => openDetail(row.id)}
        exportFilename="agent-leads-overview.csv"
      />

      <Dialog open={!!detailId} onOpenChange={(o) => !o && closeDetail()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Assignment Details</DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">Loading…</div>
          ) : !detailData ? (
            <div className="p-4 text-center text-sm text-muted-foreground">No detail data</div>
          ) : (
            <div className="space-y-2 text-[13px]">
              <div><strong>Title:</strong> {detailData.title || '-'}</div>
              <div><strong>Status:</strong> <StatusBadge status={detailData.status} /></div>
              <div><strong>Contact Group:</strong> {detailData.contact_group?.group_name || '-'}</div>
              <div><strong>Disposition Form:</strong> {detailData.disposition_form?.name || '-'}</div>
              <div><strong>Assigned To:</strong> {assignedToLabel(detailData)}</div>
              <div><strong>Assigned By:</strong> {detailData.assigned_by_user?.username || '-'}</div>
              <div><strong>Assigned At:</strong> {fmtDateTime(detailData.created_at)}</div>
              <div><strong>Completed / Total:</strong> {detailData.completed_leads || 0} / {detailData.total_leads || 0}</div>
              {detailItems.total > 0 && (
                <div>
                  <strong>Leads:</strong>
                  <div className="mt-2 max-h-[300px] overflow-y-auto rounded-md border border-border">
                    <table className="w-full border-collapse text-[12px]">
                      <thead className="sticky top-0 bg-muted/50">
                        <tr className="text-left text-muted-foreground">
                          <th className="px-2 py-1.5">Contact</th>
                          <th className="px-2 py-1.5">Status</th>
                          <th className="px-2 py-1.5">Completed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailItems.rows.map((it) => (
                          <tr key={it.id} className="border-t border-border">
                            <td className="px-2 py-1.5">{[it.contact_name, it.contact_number].filter(Boolean).join(' · ') || '-'}</td>
                            <td className={`px-2 py-1.5${it.is_deleted ? ' text-red-600 dark:text-red-400' : ''}`}>{it.is_deleted ? 'deleted' : it.status || '-'}</td>
                            <td className="px-2 py-1.5">{fmtDateTime(it.completed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <LeadsPager page={detailItems.page} total={detailItems.total} limit={detailItems.limit} onPage={detailItems.setPage} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
